import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, HStack, VStack } from "../../components/utils";
import { BlackScholesStepper } from "./components/BlackScholesStepper";
import { GrantScope } from "./components/GrantScope";
import { ValuationAssumptions } from "./components/ValuationAssumptions";
import { OtherAssumptions } from "./components/OtherAssumptions";
import { PreviewAssumptions } from "./components/PreviewAssumptions";
import { BlackScholesV2 } from "../../types/blackScholes";
import { BlackScholesSummary } from "./components/BlackScholesSummary";
import {
  useAddBlackScholesReport,
  useEditBlackScholesReport,
  useGetBlackScholesReportDetails,
} from "../../queries/blackScholes";
import Loader from "../../shared/Loader";
import BCHeader from "../../shared/BCHeader";
import { StepperNavigation } from "../../components/shared/StepperNavigation";

const BlackScholes = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [grantScopeData, setGrantScopeData] = useState<BlackScholesV2>();

  const { id } = useParams();

  const { mutate: addReport } = useAddBlackScholesReport();
  const { mutate: editReport } = useEditBlackScholesReport();
  const { data: reportDetails, isLoading } = useGetBlackScholesReportDetails(
    id || undefined
  );

  useEffect(() => {
    if (reportDetails) {
      setGrantScopeData(reportDetails);
    }
  }, [reportDetails]);

  const handleGrantScopeNext = (data: any) => {
    setGrantScopeData(data);
    setCurrentStep(2);
  };

  const steps = [
    {
      stepNo: 1,
      component: (
        <GrantScope
          onNext={handleGrantScopeNext}
          grantScopeData={grantScopeData}
        />
      ),
    },
    {
      stepNo: 2,
      component: (
        <ValuationAssumptions
          grantScopeData={grantScopeData}
          onBack={() => setCurrentStep(1)}
          onNext={() => setCurrentStep(3)}
          setGrantScopeData={setGrantScopeData}
        />
      ),
    },
    {
      stepNo: 3,
      component: (
        <OtherAssumptions
          grantScopeData={grantScopeData}
          onBack={() => setCurrentStep(2)}
          onNext={(data) => {
            setGrantScopeData(data);
            setCurrentStep(4);
          }}
          setGrantScopeData={setGrantScopeData}
        />
      ),
    },
    {
      stepNo: 4,
      component: (
        <PreviewAssumptions
          grantScopeData={grantScopeData}
          onBack={() => setCurrentStep(3)}
          onNext={() => {
            setCurrentStep(5);
          }}
          setGrantScopeData={setGrantScopeData}
        />
      ),
    },
    {
      stepNo: 5,
      component: (
        <BlackScholesSummary
          grantScopeData={grantScopeData}
          onBack={() => setCurrentStep(4)}
          onFinish={() => {
            if (grantScopeData) {
              if (id) {
                editReport(
                  { id, data: grantScopeData },
                  {
                    onSuccess(data) {
                      navigate("/options/valuationReports");
                    },
                    onError(error) {
                      toast("Error saving changes. Please try again.", {
                        type: "error",
                      });
                    },
                  }
                );
              } else {
                addReport(grantScopeData, {
                  onSuccess(data) {
                    navigate("/options/valuationReports");
                  },
                  onError(error) {
                    toast("Error saving changes. Please try again.", {
                      type: "error",
                    });
                  },
                });
              }
            }
          }}
        />
      ),
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <VStack className="h-full gap-4">
      <div className="flex items-baseline justify-between">
        <BCHeader
          className="items-baseline"
          bcTitle="Valuation Reporting"
          bcSubTitle="Black-Scholes"
        />
      </div>

      <HStack className="flex h-full min-h-full gap-4">
        {/* Left Side: Stepper */}
        <VStack className="flex w-[25%] bg-white rounded-lg min-w-72 drop-shadow-md">
          <BlackScholesStepper
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        </VStack>

        {/* Right Side: Content */}
        <VStack className="flex w-[75%] bg-white rounded-lg min-w-96 drop-shadow-md">
          <StepperNavigation
            currentStep={currentStep}
            steps={steps}
            className="p-0"
          />
        </VStack>
      </HStack>
    </VStack>
  );
};

export default BlackScholes;
